import { Store, KustomerEventTypes } from './types';
import { ChatSessionsData } from './api/sessions/types';
import { UserAttributes } from './api/users/types';
import { assistantStore } from './api/assistant/helpers';

const initialState = {
  listeners: {},
  sessions: {},
  unreadCounts: {
    conversations: {},
    total: 0,
  },
  users: {},
};

let store = initialState as Store;

export const getStore = () => {
  return store;
};

/**
 * Generic function that updates a property on the store
 * and creates a clone of the store.
 * @template M Property on store.
 */
export const updateStore = <M extends keyof Store>(
  property: M,
  data: Store[M],
) => {
  store = { ...store, [property]: data };
};

export const addListenerToStore = (event: KustomerEventTypes, callback) => {
  const currentListeners = store.listeners[event] || [];
  store = {
    ...store,
    listeners: {
      ...store.listeners,
      [event]: [...currentListeners, callback],
    },
  };
};

export const removeListenerFromStore = (
  event: KustomerEventTypes,
  callback,
) => {
  const currentListeners = store.listeners[event];

  if (!currentListeners?.length) return;

  store = {
    ...store,
    listeners: {
      ...store.listeners,
      [event]: currentListeners.filter((cb) => cb !== callback),
    },
  };
};

export const addSessionToStore = (response: ChatSessionsData) => {
  store = {
    ...store,
    sessions: {
      ...store.sessions,
      [response.id]: response,
    },
  };
};
export const getSessionFromStore = (sessionId: string) => {
  return store.sessions?.[sessionId];
};

export const addUnreadCountsToStore = (counts) => {
  store = {
    ...store,
    unreadCounts: counts,
  };
};

export const updateUnreadCountInStore = (
  sessionId,
  nextTotal,
  nextConversationCount,
) => {
  store = {
    ...store,
    unreadCounts: {
      total: nextTotal,
      conversations: {
        ...store.unreadCounts.conversations,
        [sessionId]: nextConversationCount,
      },
    },
  };
};

export const resetUnreadCountInStore = (sessionId: string) => {
  const currentConverstionCount = store.unreadCounts.conversations[sessionId];
  const currentTotal = store.unreadCounts.total;

  const nextConversationCount = 0;
  const nextTotal =
    currentTotal > 0 ? currentTotal - currentConverstionCount : 0;

  updateUnreadCountInStore(sessionId, nextTotal, nextConversationCount);
};

export const incrementUnreadCountInStore = (sessionId: string) => {
  const currentConverstionCount =
    store.unreadCounts.conversations[sessionId] || 0;
  const currentTotal = store.unreadCounts.total || 0;

  const nextConversationCount = currentConverstionCount + 1;
  const nextTotal = currentTotal + 1;

  updateUnreadCountInStore(sessionId, nextTotal, nextConversationCount);
};
export const getUnreadCountsFromStore = () => {
  return store.unreadCounts;
};

export const addUserToStore = (userId, userAttributes: UserAttributes) => {
  store = {
    ...store,
    users: {
      ...store.users,
      [userId]: userAttributes,
    },
  };
};

export const getUserFromStore = (userId) => {
  return store.users[userId];
};

export const clearUserDataFromStore = () => {
  updateStore('customerId', undefined);
  updateStore('externalId', undefined);
  updateStore('email', undefined);
  updateStore('verified', undefined);
  updateStore('sessions', {});
  updateStore('users', {});
  updateStore('unreadCounts', initialState.unreadCounts);
  assistantStore.clear();
};
